import { useState, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import Toast from '@/utils/toastManager';

const PAGE_SIZE = 20;

const UnsyncedUsers = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('desc');
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [syncingAll, setSyncingAll] = useState(false);
  const userData = useAtomValue(userInfoAtom);

  useEffect(() => {
    fetchUnsyncedUsers(1, sort, '', planFilter);
  }, [sort, planFilter]);

  useEffect(() => {
    fetchUnsyncedUsers(page, sort, search, planFilter);
  }, [page]);

  const fetchUnsyncedUsers = async (currentPage, currentSort, currentSearch, currentPlan = '') => {
    setLoading(true);
    await fetch(
      `${import.meta.env.VITE_APP_API_URL}/portal/unsynced-users?page=${currentPage}&limit=${PAGE_SIZE}&sort=${currentSort}&search=${encodeURIComponent(currentSearch)}&plan=${encodeURIComponent(currentPlan)}`,
      { headers: { Authorization: `Bearer ${userData.token}` } }
    )
      .then(async (response) => {
        if (!response.ok) {
          Toast.error('Failed to load unsynced users');
          return;
        }
        const result = await response.json();
        setData(result.data);
      })
      .finally(() => setLoading(false));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUnsyncedUsers(1, sort, search, planFilter);
  };

  const handleSync = async (email) => {
    const response = await fetch(
      `${import.meta.env.VITE_APP_API_URL}/portal/sync-subscription`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${userData.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      }
    );
    const result = await response.json();
    if (result.success) {
      Toast.success(`Synced ${email}`);
      fetchUnsyncedUsers(page, sort, search, planFilter);
    } else {
      Toast.error('Sync failed: ' + result.message);
    }
  };

  const handleSyncAll = async () => {
    setSyncingAll(true);
    const response = await fetch(
      `${import.meta.env.VITE_APP_API_URL}/portal/unsynced-users/sync-all`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${userData.token}` },
      }
    );
    const result = await response.json();
    if (result.success) {
      Toast.success(result.message);
      fetchUnsyncedUsers(1, sort, search);
      setPage(1);
    } else {
      Toast.error('Sync all failed: ' + result.message);
    }
    setSyncingAll(false);
  };

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.4rem' }}>
            <input
              type="text"
              placeholder="Search by email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ fontSize: '0.85rem', padding: '0.3rem 0.6rem', borderRadius: '0.375rem', border: '1px solid #d1d5db', minWidth: '220px' }}
            />
            <button type="submit" className="btn btn-secondary" style={{ fontSize: '0.85rem', padding: '0.3rem 0.75rem' }}>
              Search
            </button>
          </form>
          <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
            {data ? `${data.total} unsynced user${data.total !== 1 ? 's' : ''}` : ''}
          </div>
          <select
            value={planFilter}
            onChange={(e) => { setPage(1); setPlanFilter(e.target.value); }}
            style={{ fontSize: '0.85rem', padding: '0.3rem 0.5rem', borderRadius: '0.375rem', border: '1px solid #d1d5db' }}
          >
            <option value="">All Plans</option>
            <option value="standard">Standard</option>
            <option value="vip">VIP</option>
            <option value="studio">Studio</option>
          </select>
          <select
            value={sort}
            onChange={(e) => { setPage(1); setSort(e.target.value); }}
            style={{ fontSize: '0.85rem', padding: '0.3rem 0.5rem', borderRadius: '0.375rem', border: '1px solid #d1d5db' }}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-secondary" onClick={() => fetchUnsyncedUsers(page, sort, search)} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button className="btn btn-primary" onClick={handleSyncAll} disabled={syncingAll || loading || !data?.total}>
            {syncingAll ? 'Syncing...' : 'Sync All'}
          </button>
        </div>
      </div>

      {loading && !data && <div className="loading">Loading unsynced users...</div>}

      {data?.users?.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">✓</div>
          <p>{search ? 'No users found matching your search' : 'All users are in sync'}</p>
        </div>
      )}

      {data?.users?.map((user) => (
        <div key={user.userId} className="failure-item" style={{ marginBottom: '0.75rem' }}>
          <div className="failure-header">
            <div className="failure-type">{user.email}</div>
            <button
              className="btn btn-primary"
              style={{ fontSize: '0.78rem', padding: '0.3rem 0.9rem' }}
              onClick={() => handleSync(user.email)}
            >
              Sync
            </button>
          </div>
          <div className="webhook-meta" style={{ marginTop: '0.4rem' }}>
            <span>Plan: {user.planName || 'N/A'}</span>
            <span>Status: {user.subscriptionStatus}</span>
            <span>Last Updated: {user.updatedAt ? new Date(user.updatedAt).toLocaleString() : 'N/A'}</span>
            <span>
              User Type: <strong style={{ color: '#ef4444' }}>
                {user.currentUserType || 'none'}
              </strong> → {user.expectedUserType || 'none'}
            </span>
          </div>
        </div>
      ))}

      {data?.total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
          <button className="btn btn-secondary" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>
            Prev
          </button>
          <span style={{ alignSelf: 'center', fontSize: '0.875rem' }}>
            {page} / {totalPages}
          </span>
          <button className="btn btn-secondary" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}>
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default UnsyncedUsers;
